import { useMemo, useState } from 'react';
import type { DockviewApi, DockviewPanelApi } from 'dockview-react';
import { prefsStore } from '@adiox/ui';
import { ChartPanel, type ChartParams } from '../chart/ChartPanel';

const noop = () => undefined;

/**
 * The chart outside the dock: the chart panel talks to a small stand-in for its dock panel. Its settings (symbol, timeframe,
 * indicators…) are kept in their own preference, so the phone's chart and the desktop's charts do not overwrite each other.
 */
export function MobileChart() {
  const [params, setParams] = useState<ChartParams>(
    () => (prefsStore.getState().data.mobileChart as ChartParams | undefined) ?? { linked: true },
  );
  const api = useMemo(
    () =>
      ({
        id: 'mobile-chart',
        title: '',
        isActive: true, // the only chart on screen: its keyboard shortcuts always apply
        setTitle: noop,
        setActive: noop,
        // The chart subscribes to "became active / inactive"; this one is always active, so there is nothing to tell it.
        onDidActiveChange: () => ({ dispose: noop }),
        updateParameters: (next: ChartParams) => {
          setParams(next);
          prefsStore.set('mobileChart', next);
        },
        isMaximized: () => false,
        maximize: noop,
        exitMaximized: noop,
      }) as unknown as DockviewPanelApi,
    [],
  );
  const containerApi = useMemo(
    () =>
      ({
        // The chart saves "the layout" after each change; here that is the desktop layout, left as it is.
        toJSON: () => prefsStore.getState().data.layout,
        onDidMaximizedGroupChange: () => ({ dispose: noop }),
        // Moving a chart to its own window is a desktop thing: with no panel to find, the chart simply does nothing.
        getPanel: () => undefined,
      }) as unknown as DockviewApi,
    [],
  );
  return <ChartPanel params={params} api={api} containerApi={containerApi} />;
}
